/**
 * Shared UI component for UI Profile Avatar.
 * PARENT-CHILD CONTRACT: parent screens provide the data and configuration that
 * this wrapper renders or normalizes for reuse across the application.
 */
import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';

import { UiLabelTranslatePipe } from '../ui-label-translate.pipe';

/**
 * COMPONENT: Ui Profile Avatar
 * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
 * Output: Returns the documented value or updates component/service state without changing the public contract.
 * Processing: Documents the Angular declaration that connects this class to templates, dependency injection, or template transforms.
 */
@Component({
  selector: 'ui-profile-avatar',
  standalone: true,
  imports: [UiLabelTranslatePipe],
  templateUrl: './ui-profile-avatar.component.html',
  styleUrl: './ui-profile-avatar.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UiProfileAvatarComponent {
  /** PARENT-CHILD CONTRACT: imageSrc documents the input state boundary used by this file. */
  readonly imageSrc = input('');
  /** PARENT-CHILD CONTRACT: initials documents the input state boundary used by this file. */
  readonly initials = input('');
  /** PARENT-CHILD CONTRACT: alt documents the input state boundary used by this file. */
  readonly alt = input('Profile image');
  /** PARENT-CHILD CONTRACT: label documents the input state boundary used by this file. */
  readonly label = input('');
  /** PARENT-CHILD CONTRACT: hint documents the input state boundary used by this file. */
  readonly hint = input('');
  /** PARENT-CHILD CONTRACT: canDelete documents the input state boundary used by this file. */
  readonly canDelete = input(false);
  /** PARENT-CHILD CONTRACT: editLabel documents the input state boundary used by this file. */
  readonly editLabel = input('Edit image');
  /** PARENT-CHILD CONTRACT: deleteLabel documents the input state boundary used by this file. */
  readonly deleteLabel = input('Remove image');
  readonly fileSelected = output<File>();
  readonly removeClicked = output<void>();
  /** STATE OWNER: loadedImageSrc documents the signal state boundary used by this file. */
  readonly loadedImageSrc = signal('');

  /** STATE OWNER: displayInitials documents the computed state boundary used by this file. */
  readonly displayInitials = computed(() => this.initials().trim().slice(0, 2).toUpperCase());
  /** STATE OWNER: showLoadedImage documents the computed state boundary used by this file. */
  readonly showLoadedImage = computed(() => !!this.imageSrc().trim() && this.loadedImageSrc() === this.imageSrc().trim());

  /**
   * BUSINESS LOGIC: onFileSelected
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onFileSelected(event: Event): void {
    const inputElement = event.target as HTMLInputElement;
    const file = inputElement.files?.[0];

    if (!file) {
      return;
    }

    this.fileSelected.emit(file);
    inputElement.value = '';
  }

  /**
   * BUSINESS LOGIC: onRemoveClicked
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onRemoveClicked(): void {
    this.removeClicked.emit();
  }

  /**
   * BUSINESS LOGIC: onImageLoaded
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onImageLoaded(): void {
    this.loadedImageSrc.set(this.imageSrc().trim());
  }

  /**
   * BUSINESS LOGIC: onImageError
   * Inputs: Uses the parameters, injected services, route state, or signal state declared by this file.
   * Output: Returns the documented value or updates component/service state without changing the public contract.
   * Processing: Coordinates validation, mapping, emitted events, API calls, or state updates already owned by this implementation.
   */
  onImageError(): void {
    this.loadedImageSrc.set('');
  }
}
